import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { problemDetailCode, toFormError } from '@rms/core/error';
import { AppError } from '@rms/sdk/errors';
import { PageableParam, Paginated } from '@rms/sdk/models';
import { catchError, Observable, throwError } from 'rxjs';

import { PartnerEmailInUseError, PartnerSlugInUseError } from '../errors';
import { Partner, PARTNER_ERROR_CODE, PartnerCreateRequest, PartnerPatchRequest } from '../models';
import { PartnerApiService } from './partner-api.service';

@Injectable({
  providedIn: 'root',
})
export class PartnerService {
  private readonly api = inject(PartnerApiService);

  public getPartners(pageable: PageableParam): Observable<Paginated<Partner>> {
    return this.api
      .getPartners(pageable)
      .pipe(
        catchError((error: HttpErrorResponse) => throwError(() => AppError.from(error, 'partners.errors.load-list')))
      );
  }

  public getPartner(partnerId: string): Observable<Partner> {
    return this.api
      .getPartner(partnerId)
      .pipe(
        catchError((error: HttpErrorResponse) =>
          throwError(() =>
            AppError.from(
              error,
              error.status === HttpStatusCode.NotFound ? 'partners.errors.not-found' : 'partners.errors.load'
            )
          )
        )
      );
  }

  public createPartner(request: PartnerCreateRequest): Observable<Partner> {
    return this.api.createPartner(request).pipe(
      catchError((error: HttpErrorResponse) => {
        switch (problemDetailCode(error)) {
          case PARTNER_ERROR_CODE.SLUG_ALREADY_IN_USE:
            return throwError(() => new PartnerSlugInUseError(error));
          case PARTNER_ERROR_CODE.EMAIL_ALREADY_IN_USE:
            return throwError(() => new PartnerEmailInUseError(error));
        }

        return throwError(() => toFormError(error, 'partners.errors.create'));
      })
    );
  }

  public updatePartner(partnerId: string, request: PartnerPatchRequest): Observable<Partner> {
    return this.api
      .updatePartner(partnerId, request)
      .pipe(
        catchError((error: HttpErrorResponse) =>
          throwError(() =>
            error.status === HttpStatusCode.NotFound
              ? AppError.from(error, 'partners.errors.not-found')
              : toFormError(error, 'partners.errors.update')
          )
        )
      );
  }

  public deletePartner(partnerId: string): Observable<void> {
    return this.api.deletePartner(partnerId).pipe(
      catchError((error: HttpErrorResponse) => {
        const hasCustomers = problemDetailCode(error) === PARTNER_ERROR_CODE.HAS_ORGANIZATIONS;

        return throwError(() =>
          AppError.from(error, hasCustomers ? 'partners.errors.delete-has-customers' : 'partners.errors.delete')
        );
      })
    );
  }
}
